import api, { ApiError } from './api';

const normalizeDate = (value) => {
  if (!value) {
    return null;
  }

  if (value instanceof Date) {
    return value.toISOString();
  }

  if (typeof value === 'string') {
    return value;
  }

  if (typeof value.toDate === 'function') {
    return value.toDate().toISOString();
  }

  const hasSeconds = typeof value.seconds === 'number' || typeof value._seconds === 'number';
  const hasNanos = typeof value.nanoseconds === 'number' || typeof value._nanoseconds === 'number';

  if (hasSeconds || hasNanos) {
    const seconds = typeof value.seconds === 'number' ? value.seconds : value._seconds || 0;
    const nanos = typeof value.nanoseconds === 'number' ? value.nanoseconds : value._nanoseconds || 0;
    const millis = (seconds * 1000) + Math.floor(nanos / 1_000_000);
    return new Date(millis).toISOString();
  }

  return null;
};

const normalizeId = (value) => {
  if (value === null || value === undefined) {
    return null;
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed.length ? trimmed : null;
  }

  if (typeof value === 'object') {
    if (typeof value.id === 'string') {
      return value.id;
    }

    if (typeof value._id === 'string') {
      return value._id;
    }

    if (typeof value.path === 'string') {
      const segments = value.path.split('/');
      const lastSegment = segments[segments.length - 1];
      return lastSegment || null;
    }
  }

  try {
    const stringValue = value.toString();
    if (stringValue && stringValue !== '[object Object]') {
      return stringValue;
    }
  } catch (error) {
    // Ignore errors from toString conversions
  }

  return null;
};

const resolveVehicleDescriptor = (value, fallback = null) => {
  if (!value && fallback) {
    return resolveVehicleDescriptor(fallback, null);
  }

  if (!value) {
    return null;
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed.length ? trimmed : null;
  }

  if (typeof value === 'object') {
    const potentialKeys = [
      'vehicleNumber',
      'vehicleId',
      'unitNumber',
      'number',
      'identifier',
      'plateNumber',
      'licensePlate',
      'displayName',
      'name',
      'vin',
      'id'
    ];

    for (const key of potentialKeys) {
      if (typeof value[key] === 'string' && value[key].trim()) {
        return value[key].trim();
      }
    }
  }

  return null;
};

const toTitleCase = (value, fallback = 'Unknown') => {
  if (!value || typeof value !== 'string') {
    return fallback;
  }
  const lower = value.toLowerCase();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
};

const generateFallbackId = () => `driver_${Math.random().toString(36).slice(2, 11)}`;

const formatPhoneNumber = (value) => {
  if (!value || typeof value !== 'string') {
    return value || '';
  }

  const digits = value.replace(/\D/g, '');
  if (!digits) {
    return value.trim();
  }

  if (digits.length === 10) {
    return `+1${digits}`;
  }

  if (digits.length === 11 && digits.startsWith('1')) {
    return `+${digits}`;
  }

  if (value.trim().startsWith('+')) {
    return value.trim();
  }

  return `+${digits}`;
};

const toIsoDateString = (value, fallbackIso = null) => {
  if (!value) {
    return fallbackIso;
  }

  if (value instanceof Date) {
    return value.toISOString();
  }

  if (typeof value === 'string') {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? fallbackIso : parsed.toISOString();
  }

  if (typeof value.toDate === 'function') {
    const parsed = value.toDate();
    return parsed instanceof Date && !Number.isNaN(parsed.getTime()) ? parsed.toISOString() : fallbackIso;
  }

  if (typeof value.seconds === 'number') {
    return new Date(value.seconds * 1000).toISOString();
  }

  if (typeof value._seconds === 'number') {
    return new Date(value._seconds * 1000).toISOString();
  }

  return fallbackIso;
};

const sanitizeObject = (obj) => {
  if (!obj || typeof obj !== 'object') {
    return obj;
  }

  Object.keys(obj).forEach((key) => {
    const value = obj[key];

    if (value === undefined || value === null || (typeof value === 'string' && value.trim() === '')) {
      delete obj[key];
      return;
    }

    if (Array.isArray(value)) {
      return;
    }

    if (typeof value === 'object') {
      sanitizeObject(value);
      if (!Object.keys(value).length) {
        delete obj[key];
      }
    }
  });

  return obj;
};

const normalizeDocumentsStatus = (driver = {}) => {
  const normalizedMap = new Map();

  const upsertDocument = (rawDoc, fallbackId) => {
    if (!rawDoc) {
      return;
    }

    const doc = typeof rawDoc === 'object' ? { ...rawDoc } : { value: rawDoc };

    const candidateId = doc.documentId
      || doc.id
      || doc.key
      || fallbackId
      || (typeof doc.documentType === 'string' ? doc.documentType : null)
      || undefined;

    const normalizedId = candidateId || `doc_${normalizedMap.size}`;

    const expirationDate = normalizeDate(doc.expirationDate || doc.expiryDate || doc.expiresAt);
    const issueDate = normalizeDate(doc.issueDate || doc.issuedAt);
    const effectiveDate = normalizeDate(doc.effectiveDate);
    const addedAt = normalizeDate(doc.addedAt);
    const updatedAt = normalizeDate(doc.updatedAt);

    let documentType = doc.documentType || doc.type || doc.category || null;
    if (!documentType && typeof doc.title === 'string') {
      documentType = doc.title.trim() || documentType;
    }

    const title = doc.title
      || doc.name
      || documentType
      || 'Document';

    const status = doc.status
      || doc.state
      || (doc.isExpiring ? 'expiring' : 'active');

    const parseDays = (value) => {
      if (value === undefined || value === null) {
        return null;
      }
      const numeric = typeof value === 'number' ? value : Number(value);
      return Number.isFinite(numeric) ? numeric : null;
    };

    const normalizedEntry = {
      ...doc,
      id: normalizedId,
      documentId: normalizedId,
      documentType: documentType || null,
      type: documentType || doc.type || doc.category || null,
      title,
      status,
      expirationDate,
      issueDate,
      effectiveDate,
      addedAt,
      updatedAt,
      isExpiring: typeof doc.isExpiring === 'boolean' ? doc.isExpiring : null,
      daysUntilExpiration: parseDays(doc.daysUntilExpiration)
    };

    if (normalizedEntry.isExpiring === null && typeof normalizedEntry.daysUntilExpiration === 'number') {
      normalizedEntry.isExpiring = normalizedEntry.daysUntilExpiration <= 30 && normalizedEntry.daysUntilExpiration >= 0;
    }

    const existing = normalizedMap.get(normalizedId);
    if (existing) {
      normalizedMap.set(normalizedId, {
        ...normalizedEntry,
        ...existing,
        status: existing.status || normalizedEntry.status,
        expirationDate: existing.expirationDate || normalizedEntry.expirationDate,
        issueDate: existing.issueDate || normalizedEntry.issueDate,
        effectiveDate: existing.effectiveDate || normalizedEntry.effectiveDate,
        addedAt: existing.addedAt || normalizedEntry.addedAt,
        updatedAt: existing.updatedAt || normalizedEntry.updatedAt,
        title: existing.title || normalizedEntry.title,
        documentType: existing.documentType || normalizedEntry.documentType,
        type: existing.type || normalizedEntry.type,
        isExpiring: typeof existing.isExpiring === 'boolean' ? existing.isExpiring : normalizedEntry.isExpiring,
        daysUntilExpiration: existing.daysUntilExpiration ?? normalizedEntry.daysUntilExpiration
      });
    } else {
      normalizedMap.set(normalizedId, normalizedEntry);
    }
  };

  if (Array.isArray(driver.documentsStatus)) {
    driver.documentsStatus
      .filter(Boolean)
      .forEach((doc, index) => {
        const fallbackId = doc?.documentId || doc?.id || `legacy_${index}`;
        upsertDocument(doc, fallbackId);
      });
  }

  if (driver.documents && typeof driver.documents === 'object') {
    Object.entries(driver.documents).forEach(([docId, value]) => {
      upsertDocument(value, docId);
    });
  }

  return Array.from(normalizedMap.values());
};

const resolveStatusValue = (status, fallbackStatus, fallbackIsActive) => {
  if (typeof status === 'string' && status.trim()) {
    return status.trim().toLowerCase();
  }

  if (typeof fallbackStatus === 'string' && fallbackStatus.trim()) {
    return fallbackStatus.trim().toLowerCase();
  }

  if (typeof fallbackIsActive === 'boolean') {
    return fallbackIsActive ? 'active' : 'inactive';
  }

  return undefined;
};

// CDL numbers are often written with spaces or dashes (e.g. FL "A123-456-78-901-0");
// the backend stores them as plain uppercase letters and digits.
const normalizeCdlNumber = (value) => {
  if (typeof value !== 'string') {
    return undefined;
  }
  const normalized = value.replace(/[\s-]/g, '').toUpperCase();
  return normalized || undefined;
};

// Sends only the fields DriverForm collects. Fields the form no longer shows
// (medical certificate, endorsement/restriction codes) are left untouched on update.
const buildDriverPayload = (formData = {}, {
  mode = 'create',
  companyId = null,
  defaults = {},
  includeStatusDefaults = false
} = {}) => {
  const normalizedAddressState = (formData.state || '').trim().toUpperCase().slice(0, 2);
  const normalizedLicenseStateSource = (formData.licenseState || normalizedAddressState || '').trim();
  const normalizedLicenseState = normalizedLicenseStateSource ? normalizedLicenseStateSource.toUpperCase().slice(0, 2) : undefined;

  const resolveDateValue = (value, fallbackKeys = []) => {
    if (value !== undefined && value !== null && `${value}`.trim() !== '') {
      return value;
    }

    for (const key of fallbackKeys) {
      const fallbackValue = defaults?.[key];
      if (fallbackValue !== undefined && fallbackValue !== null) {
        return fallbackValue;
      }
    }

    return undefined;
  };

  const hireDate = toIsoDateString(
    resolveDateValue(formData.hireDate, ['hireDate', 'originalHireDate'])
  );

  if (mode === 'create' && !hireDate) {
    throw new Error('Hire date is required to create a driver');
  }

  const statusValue = resolveStatusValue(
    formData.status,
    defaults?.status,
    typeof formData.isActive === 'boolean' ? formData.isActive : defaults?.isActive
  );

  const basePayload = {
    firstName: formData.firstName?.trim() || defaults?.firstName || undefined,
    lastName: formData.lastName?.trim() || defaults?.lastName || undefined,
    email: formData.email?.trim() || defaults?.email || undefined,
    phone: formatPhoneNumber(formData.phone || defaults?.phone),
    address: formData.address?.trim() || defaults?.address || undefined,
    city: formData.city?.trim() || defaults?.city || undefined,
    state: normalizedAddressState || defaults?.state || undefined,
    zipCode: formData.zipCode?.trim() || defaults?.zipCode || undefined,
    dateOfBirth: toIsoDateString(
      resolveDateValue(formData.dateOfBirth, ['dateOfBirth'])
    ),
    hireDate,
    cdlNumber: normalizeCdlNumber(formData.cdlNumber) || defaults?.cdlNumber || undefined,
    cdlClass: formData.cdlClass || defaults?.cdlClass || (mode === 'create' ? 'Class A' : undefined),
    cdlIssueDate: toIsoDateString(
      resolveDateValue(formData.cdlIssueDate, ['cdlIssueDate'])
    ),
    cdlExpirationDate: toIsoDateString(
      resolveDateValue(formData.cdlExpirationDate, ['cdlExpirationDate'])
    ),
    licenseState: normalizedLicenseState || defaults?.licenseState || undefined,
    hazmatEndorsement: typeof formData.hazmatEndorsement !== 'undefined'
      ? Boolean(formData.hazmatEndorsement)
      : Boolean(defaults?.hazmatEndorsement),
    passengerEndorsement: typeof formData.passengerEndorsement !== 'undefined'
      ? Boolean(formData.passengerEndorsement)
      : Boolean(defaults?.passengerEndorsement),
    schoolBusEndorsement: typeof formData.schoolBusEndorsement !== 'undefined'
      ? Boolean(formData.schoolBusEndorsement)
      : Boolean(defaults?.schoolBusEndorsement),
    doubleTripleEndorsement: typeof formData.doubleTripleEndorsement !== 'undefined'
      ? Boolean(formData.doubleTripleEndorsement)
      : Boolean(defaults?.doubleTripleEndorsement),
    status: statusValue,
    isActive: typeof statusValue === 'string'
      ? statusValue !== 'inactive'
      : (typeof formData.isActive === 'boolean' ? formData.isActive : defaults?.isActive),
    emergencyContact1: {
      name: formData.emergencyContact1Name?.trim() || defaults?.emergencyContact1?.name || undefined,
      relationship: formData.emergencyContact1Relationship?.trim() || defaults?.emergencyContact1?.relationship || undefined,
      phone: formatPhoneNumber(formData.emergencyContact1Phone || defaults?.emergencyContact1?.phone),
      address: formData.emergencyContact1Address?.trim() || defaults?.emergencyContact1?.address || undefined
    }
  };

  const secondaryContact = {
    name: formData.emergencyContact2Name?.trim() || defaults?.emergencyContact2?.name,
    relationship: formData.emergencyContact2Relationship?.trim() || defaults?.emergencyContact2?.relationship,
    phone: formatPhoneNumber(formData.emergencyContact2Phone || defaults?.emergencyContact2?.phone),
    address: formData.emergencyContact2Address?.trim() || defaults?.emergencyContact2?.address
  };

  if (secondaryContact.name || secondaryContact.relationship || secondaryContact.phone || secondaryContact.address) {
    basePayload.emergencyContact2 = sanitizeObject({ ...secondaryContact });
  }

  if (mode === 'create') {
    basePayload.driverId = formData.driverId?.trim() || defaults.driverId;
    basePayload.employeeId = formData.employeeId?.trim() || defaults.employeeId;
    basePayload.companyId = companyId;
    basePayload.endorsements = [];
    basePayload.restrictions = [];

    if (includeStatusDefaults) {
      basePayload.status = basePayload.status || 'active';
      basePayload.isActive = typeof basePayload.isActive === 'boolean'
        ? basePayload.isActive
        : true;
    }
  } else {
    const resolvedDriverId = formData.driverId?.trim() || defaults?.driverId || defaults?.id;
    if (resolvedDriverId) {
      basePayload.driverId = resolvedDriverId;
    }

    const resolvedEmployeeId = formData.employeeId?.trim() || defaults?.employeeId;
    if (resolvedEmployeeId) {
      basePayload.employeeId = resolvedEmployeeId;
    }

    const resolvedCompanyId = companyId || defaults?.companyId;
    if (resolvedCompanyId) {
      basePayload.companyId = resolvedCompanyId;
    }
  }

  return sanitizeObject(basePayload);
};

class DriverService {
  constructor() {
    this.basePath = '/drivers';
  }

  normalizeDriver(driver = {}) {
    const firstName = driver.firstName?.trim() || '';
    const lastName = driver.lastName?.trim() || '';
    const fullName = `${firstName} ${lastName}`.trim();

    const medicalExpiration = driver.medicalExpirationDate || driver.medicalCertExpiration;

    const assignedVehicles = Array.isArray(driver.assignedVehicles)
      ? Array.from(
          new Set(
            driver.assignedVehicles
              .map((vehicleId) => normalizeId(vehicleId))
              .filter(Boolean)
          )
        )
      : [];

    const resolvedStatus = driver.status
      || (typeof driver.isActive === 'boolean' ? (driver.isActive ? 'active' : 'inactive') : undefined)
      || 'active';

    const hireDateSource = driver.hireDate
      ?? driver.employment?.hireDate
      ?? driver.employment?.startDate
      ?? driver.dateOfHire
      ?? driver.hire_date
      ?? null;

    const hireDate = normalizeDate(hireDateSource);
    let experienceYears = driver.experience;
    if (experienceYears === undefined && hireDate) {
      const hire = new Date(hireDate);
      if (!Number.isNaN(hire.getTime())) {
        const diff = Date.now() - hire.getTime();
        experienceYears = Math.max(0, Math.floor(diff / (365.25 * 24 * 60 * 60 * 1000)));
      }
    }

    const currentVehicleId = normalizeId(driver.currentVehicleId) || assignedVehicles[0] || null;

    const currentVehicleNumber = resolveVehicleDescriptor(driver.currentVehicleNumber)
      || resolveVehicleDescriptor(driver.currentVehicle)
      || resolveVehicleDescriptor(driver.currentVehicleName)
      || resolveVehicleDescriptor(driver.activeVehicle)
      || resolveVehicleDescriptor(driver.assignedVehicleNumber)
      || resolveVehicleDescriptor(driver.vehicleAssignment)
      || currentVehicleId;

    return {
      ...driver,
      id: driver.id || driver.driverId || driver.employeeId || generateFallbackId(),
      name: fullName || driver.name || driver.driverId || 'Unnamed Driver',
      licenseStatus: toTitleCase(driver.licenseStatus || driver.status || (driver.isActive ? 'active' : 'inactive'), 'Active'),
      status: resolvedStatus,
      isActive: typeof driver.isActive === 'boolean' ? driver.isActive : resolvedStatus !== 'inactive',
      medicalCertExpiration: normalizeDate(medicalExpiration),
      medicalExpirationDate: normalizeDate(driver.medicalExpirationDate),
      medicalExaminationDate: normalizeDate(driver.medicalExaminationDate),
      dateOfBirth: normalizeDate(driver.dateOfBirth),
      hireDate,
      originalHireDate: hireDateSource,
      cdlIssueDate: normalizeDate(driver.cdlIssueDate),
      cdlExpirationDate: normalizeDate(driver.cdlExpirationDate),
      createdAt: normalizeDate(driver.createdAt),
      updatedAt: normalizeDate(driver.updatedAt),
      documentsStatus: normalizeDocumentsStatus(driver),
      hosStatus: driver.hosStatus || 'Available',
      hosData: driver.hosData || null,
      licenseClass: driver.licenseClass || driver.cdlClass || 'Class A',
      cdlClass: driver.cdlClass || driver.licenseClass || 'Class A',
      safetyScore: driver.safetyScore ?? 100,
      experience: experienceYears ?? 0,
      email: driver.email || '',
      phone: driver.phone || '',
      companyId: driver.companyId || null,
      assignedVehicles,
      currentVehicleId,
      currentVehicleNumber,
      currentVehicle: currentVehicleNumber,
      currentVehicleAssignedAt: normalizeDate(driver.currentVehicleAssignedAt || driver.vehicleAssignedAt)
    };
  }

  async listDrivers(params = {}, { companyId } = {}) {
    try {
      const config = { params };

      if (companyId) {
        config.headers = { 'x-company-id': companyId };
      }

      const response = await api.get(this.basePath, config);
      const data = response?.data ?? response ?? {};

      const rawDrivers = Array.isArray(data?.drivers) ? data.drivers : [];
      const normalizedDrivers = rawDrivers.map((driver) => this.normalizeDriver(driver));

      return {
        drivers: normalizedDrivers,
        rawDrivers,
        pagination: data?.pagination || {
          total: normalizedDrivers.length,
          page: 1,
          totalPages: 1,
          limit: normalizedDrivers.length,
        },
      };
    } catch (error) {
      throw error instanceof ApiError ? error : ApiError.fromResponse(error);
    }
  }

  async createDriver(formData, { companyId } = {}) {
    try {
      if (!companyId) {
        throw new Error('Company ID is required to create a driver');
      }

      const timestamp = Date.now();
      const generatedDriverId = `DRV-${timestamp.toString(36).toUpperCase()}`;
      const generatedEmployeeId = `EMP-${timestamp.toString(36).toUpperCase()}`;

      const payload = buildDriverPayload(formData, {
        mode: 'create',
        companyId,
        defaults: {
          driverId: generatedDriverId,
          employeeId: generatedEmployeeId
        },
        includeStatusDefaults: true
      });

      const response = await api.post(this.basePath, payload, {
        headers: { 'x-company-id': companyId }
      });

      const createdDriver = response?.data?.driver || response?.driver || response;
      return this.normalizeDriver(createdDriver);
    } catch (error) {
      throw error instanceof ApiError ? error : ApiError.fromResponse(error);
    }
  }

  /**
   * The driver record for a signed-in driver user. Prefers the explicit link
   * written at hire (users.driverId), then falls back to matching by email or
   * legacy uid-based driver code for drivers created before that link existed.
   */
  async findDriverForUser(userData, { companyId } = {}) {
    if (!userData || !companyId) return null;
    if (userData.driverId) {
      try {
        const linked = await this.getDriverById(userData.driverId, { companyId });
        if (linked?.id && (!linked.companyId || linked.companyId === companyId)) return linked;
      } catch (error) {
        if (error?.status && error.status !== 404) throw error;
      }
    }
    const email = (userData.email || '').toLowerCase();
    if (!email) return null;
    const response = await this.listDrivers({ search: email, limit: 50 }, { companyId });
    return response.drivers.find((driver) => driver.email?.toLowerCase() === email)
      || response.drivers.find((driver) => driver.driverId?.toLowerCase() === userData.uid?.toLowerCase())
      || null;
  }

  async getDriverById(driverId, { companyId } = {}) {
    if (!driverId) {
      throw new Error('Driver ID is required');
    }

    try {
      const config = {};
      if (companyId) {
        config.headers = { 'x-company-id': companyId };
      }

      const response = await api.get(`${this.basePath}/${driverId}`, config);
      const driver = response?.data?.driver || response?.driver || response;
      return this.normalizeDriver(driver);
    } catch (error) {
      throw error instanceof ApiError ? error : ApiError.fromResponse(error);
    }
  }

  async updateDriver(driverId, formData, { companyId, defaults } = {}) {
    if (!driverId) {
      throw new Error('Driver ID is required to update a driver');
    }

    try {
      const payload = buildDriverPayload(formData, {
        mode: 'update',
        companyId,
        defaults: defaults || {}
      });

      const config = {};
      if (companyId) {
        config.headers = { 'x-company-id': companyId };
      }

      const response = await api.put(`${this.basePath}/${driverId}`, payload, config);
      const updatedDriver = response?.data?.driver || response?.driver || response;
      return this.normalizeDriver(updatedDriver);
    } catch (error) {
      throw error instanceof ApiError ? error : ApiError.fromResponse(error);
    }
  }

  async updateDriverStatus(driverId, status, { companyId, reason } = {}) {
    if (!driverId) {
      throw new Error('Driver ID is required to update status');
    }

    if (!status) {
      throw new Error('Status is required');
    }

    try {
      const payload = {
        status,
        reason: reason?.trim() || undefined
      };

      const config = {};
      if (companyId) {
        config.headers = { 'x-company-id': companyId };
      }

      const response = await api.put(`${this.basePath}/${driverId}/status`, payload, config);
      const updatedDriver = response?.data?.driver || response?.driver || response;
      return this.normalizeDriver(updatedDriver);
    } catch (error) {
      throw error instanceof ApiError ? error : ApiError.fromResponse(error);
    }
  }

  async deleteDriver(driverId, { companyId } = {}) {
    if (!driverId) {
      throw new Error('Driver ID is required to delete a driver');
    }

    try {
      const config = {};
      if (companyId) {
        config.headers = { 'x-company-id': companyId };
      }

      const response = await api.delete(`${this.basePath}/${driverId}`, config);
      return response?.data || response || { success: true };
    } catch (error) {
      throw error instanceof ApiError ? error : ApiError.fromResponse(error);
    }
  }
}

const driverService = new DriverService();
export default driverService;
